import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Setup } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Button, Field, TextInput } from "@/components/ui";
import { PageHeader } from "@/components/AppShell";
import {
  authError,
  CodeField,
  RecoveryCodes,
  SetupAuthenticator,
} from "./AdminAuthFlow";
export function SecurityPage() {
  const { t } = useTranslation();
  const { complete } = useAuth();
  const [security, setSecurity] = useState<Awaited<
    ReturnType<typeof api.security>
  > | null>(null);
  const [action, setAction] = useState<"codes" | "reset" | null>(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<Setup | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    api
      .security()
      .then((value) => {
        if (active) setSecurity(value);
      })
      .catch((caught) => {
        if (active) setError(authError(caught, t));
      });
    return () => {
      active = false;
    };
  }, [t]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (action === "codes") {
        const result = await api.recoveryCodes(password, code);
        setCodes(result.recovery_codes);
      } else {
        setSetup(await api.resetTotp(password, code));
      }
      setPassword("");
      setCode("");
    } catch (caught) {
      setError(authError(caught, t));
    } finally {
      setBusy(false);
    }
  }
  function close() {
    setCodes(null);
    setSetup(null);
    setAction(null);
    setPassword("");
    setCode("");
    void api
      .security()
      .then(setSecurity)
      .catch(() => {});
  }
  return (
    <div className="max-w-2xl">
      <PageHeader
        title={t("adminAuth.securityTitle")}
        subtitle={t("adminAuth.securityHelp")}
      />
      <div className="card space-y-5 p-5">
        {security && (
          <div className="space-y-1 text-sm text-ink">
            <p className="font-medium">
              {t(
                security.enabled
                  ? "adminAuth.twoFactorActive"
                  : "adminAuth.twoFactorInactive",
              )}
            </p>
            <p>
              {t("adminAuth.codesRemaining", {
                count: security.recovery_codes_remaining,
              })}
            </p>
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        {codes ? (
          <RecoveryCodes codes={codes} onDone={close} />
        ) : setup ? (
          <SetupAuthenticator
            setup={setup}
            onConfirmed={(value) => {
              complete(value);
              setSetup(null);
              setCodes(value.recovery_codes);
            }}
            onBack={close}
          />
        ) : action ? (
          <form onSubmit={submit} className="space-y-4">
            <h2 className="font-semibold text-ink">
              {t(
                action === "codes"
                  ? "adminAuth.newCodes"
                  : "adminAuth.newAuthenticator",
              )}
            </h2>
            <Field label={t("login.password")}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>
            <CodeField code={code} setCode={setCode} />
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="primary"
                loading={busy}
                disabled={!password || code.length !== 6}
              >
                {t("adminAuth.continue")}
              </Button>
              <Button onClick={close}>{t("common.cancel")}</Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setAction("codes")}>
              {t("adminAuth.newCodes")}
            </Button>
            <Button onClick={() => setAction("reset")}>
              {t("adminAuth.newAuthenticator")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
